import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import {
  createBrandSpecificationRequest,
  deleteBrandSpecificationRequest,
  loadSpecificationByIdRequest,
  updateBasicDataSpecificationRequest,
  updateLabSpecificationRequest,
} from '../../../redux/specificationRedux';
import {
  Autocomplete,
  Box,
  Button,
  Checkbox,
  Divider,
  FormControl,
  FormControlLabel,
  InputLabel,
  OutlinedInput,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import { getUser } from '../../../redux/usersRedux';
import { createLabelSpecification } from '../../../utils/createLabelSpecification';

import { getBrands, loadBrandsRequest } from '../../../redux/brandsRedux';

const DECISION_MAKERS = ['Dyrektor', 'Szef rozlewu'];

const EditSpecification = () => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const { id } = useParams();
  const user = useSelector((state) => getUser(state));
  const [specification, setSpecification] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editRowId, setEditRowId] = useState(null);
  const [isLineSpecific, setIsLineSpecific] = useState(false);
  const [name, setName] = useState(specification ? specification.name : '');
  const [unit, setUnit] = useState(specification ? specification.unit : '');
  const [specMin, setSpecMin] = useState('');
  const [specNominal, setSpecNominal] = useState('');
  const [specMax, setSpecMax] = useState('');
  const [warehouse, setWarehouse] = useState('');
  const [line, setLine] = useState('');
  const [minBMin, setMinBMin] = useState('');
  const [minBMax, setMinBMax] = useState('');
  const [idBMin, setIdBMin] = useState('');
  const [idBMax, setIdBMax] = useState('');
  const [idXMin, setIdXMin] = useState('');
  const [idXMax, setIdXMax] = useState('');
  const [maxBMin, setMaxBMin] = useState('');
  const [maxBMax, setMaxBMax] = useState('');
  const [maxXMin, setMaxXMin] = useState('');
  const [maxXMax, setMaxXMax] = useState('');
  const [minXMin, setMinXMin] = useState('');
  const [minXMax, setMinXMax] = useState('');
  const [newSpecMin, setNewSpecMin] = useState('');
  const [newSpecNominal, setNewSpecNominal] = useState('');
  const [newSpecMax, setNewSpecMax] = useState('');
  const [newWarehouse, setNewWarehouse] = useState('');
  const [newLine, setNewLine] = useState('');
  const [newMinBMin, setNewMinBMin] = useState('');
  const [newMinBMax, setNewMinBMax] = useState('');
  const [newMaxBMin, setNewMaxBMin] = useState('');
  const [newMaxBMax, setNewMaxBMax] = useState('');
  const [newMaxXMin, setNewMaxXMin] = useState('');
  const [newMaxXMax, setNewMaxXMax] = useState('');
  const [newMinXMin, setNewMinXMin] = useState('');
  const [newMinXMax, setNewMinXMax] = useState('');
  const [brandId, setBrandId] = useState('');
  const brands = useSelector((state) => getBrands(state));
  const [reload, setReload] = useState(0);
  const [decisionMaker, setDecisionMaker] = useState(
    specification ? specification.decisionMaker : '',
  );

  useEffect(() => {
    const start = async () => {
      const res = await dispatch(loadSpecificationByIdRequest(id));
      await dispatch(loadBrandsRequest());
      setSpecification(res.data);
      setName(res.data.name);
      setUnit(res.data.unit);
      setIsLineSpecific(res.data.isLineSpecific);
      setDecisionMaker(res.data.decisionMaker);
      setLoading(false);
    };
    start();
  }, [dispatch, id, reload]);

  const startEditing = (row) => {
    setEditRowId(row.id);

    setWarehouse(row.warehouse ?? '');
    setLine(row.line ?? '');

    setSpecMin(row.specMin ?? '');
    setSpecNominal(row.specNominal ?? '');
    setSpecMax(row.specMax ?? '');
    const zMinX = row.labZones?.find((z) => z.descriptionOrder === 'minX');
    const zMinB = row.labZones?.find((z) => z.descriptionOrder === 'minB');
    const zMaxB = row.labZones?.find((z) => z.descriptionOrder === 'maxB');
    const zMaxX = row.labZones?.find((z) => z.descriptionOrder === 'maxX');
    setMinBMin(zMinB?.min ?? '');
    setMinBMax(zMinB?.max ?? '');
    setIdBMin(zMinB?.id ?? '');
    setMaxBMin(zMaxB?.min ?? '');
    setMaxBMax(zMaxB?.max ?? '');
    setIdBMax(zMaxB?.id ?? '');
    setMaxXMin(zMaxX?.min ?? '');
    setMaxXMax(zMaxX?.max ?? '');
    setIdXMin(zMaxX?.id ?? '');
    setMinXMin(zMinX?.min ?? '');
    setMinXMax(zMinX?.max ?? '');
    setIdXMax(zMinX?.id ?? '');
    console.log('Editing row zones:', { zMinX, zMinB, zMaxB, zMaxX });
  };

  const cancelEditing = () => {
    setEditRowId(null);
    setWarehouse('');
    setLine('');
    setSpecMin('');
    setSpecNominal('');
    setSpecMax('');
    setMinBMin('');
    setMinBMax('');
    setMaxBMin('');
    setMaxBMax('');
    setMaxXMin('');
    setMaxXMax('');
    setMinXMin('');
    setMinXMax('');
    setIdBMin('');
    setIdBMax('');
    setIdXMin('');
    setIdXMax('');
  };

  const handleEditBasicData = async (e) => {
    e.preventDefault();
    const editedBasicDataSpecification = {
      id: specification.id,
      name,
      unit,
      isLineSpecific,
      decisionMaker: decisionMaker,
    };
    const res = await dispatch(
      updateBasicDataSpecificationRequest(editedBasicDataSpecification),
    );
    setReload((prev) => prev + 1);
  };

  const handleEditBrandSpecification = async () => {
    const specData = {
      specMin,
      specNominal,
      specMax,
    };
    const minB = {
      minBMin,
      minBMax,
    };
    const minX = {
      minXMin,
      minXMax,
    };
    const maxB = {
      maxBMin,
      maxBMax,
    };
    const maxX = {
      maxXMin,
      maxXMax,
    };

    const specLabel = createLabelSpecification('nominal', specData);
    const labZoneBMin = createLabelSpecification('minB', minB);
    const labZoneXMin = createLabelSpecification('minX', minX);
    const labZoneBMax = createLabelSpecification('maxB', maxB);
    const labZoneXMax = createLabelSpecification('maxX', maxX);
    const labZones = [
      { id: idBMin, ...labZoneBMin },
      { id: idXMin, ...labZoneXMin },
      { id: idBMax, ...labZoneBMax },
      { id: idXMax, ...labZoneXMax },
    ].filter((z) => z != null); // usuwa null i undefined

    const data = {
      id: editRowId,
      warehouse: warehouse,
      line: line,
      specMin: specMin !== '' ? parseFloat(specMin) : null,
      specNominal: specNominal !== '' ? parseFloat(specNominal) : null,
      specMax: specMax !== '' ? parseFloat(specMax) : null,
      specLabel: specLabel,
      labZone: labZones,
    };

    const res = await dispatch(updateLabSpecificationRequest(data));
    cancelEditing();
    setReload((prev) => prev + 1);
  };
  const handleSubmit = async (e) => {
    e.preventDefault();
    const specData = {
      specMin: newSpecMin,
      specNominal: newSpecNominal,
      specMax: newSpecMax,
    };
    const minB = {
      minBMin: newMinBMin,
      minBMax: newMinBMax,
    };
    const minX = {
      minXMin: newMinXMin,
      minXMax: newMinXMax,
    };
    const maxB = {
      maxBMin: newMaxBMin,
      maxBMax: newMaxBMax,
    };
    const maxX = {
      maxXMin: newMaxXMin,
      maxXMax: newMaxXMax,
    };

    const specLabel = createLabelSpecification('nominal', specData);
    const labZoneBMin = createLabelSpecification('minB', minB);
    const labZoneXMin = createLabelSpecification('minX', minX);
    const labZoneBMax = createLabelSpecification('maxB', maxB);
    const labZoneXMax = createLabelSpecification('maxX', maxX);

    const labZones = [
      labZoneBMin,
      labZoneXMin,
      labZoneBMax,
      labZoneXMax,
    ].filter((z) => z != null); // usuwa null i undefined
    // usuwa null i undefined

    const data = {
      parameterId: specification.id,
      brandId: brandId,
      warehouse: newWarehouse,
      line: newLine,
      specMin: newSpecMin !== '' ? parseFloat(newSpecMin) : null,
      specNominal: newSpecNominal !== '' ? parseFloat(newSpecNominal) : null,
      specMax: newSpecMax !== '' ? parseFloat(newSpecMax) : null,
      specLabel: specLabel,
      labZone: labZones,
    };

    const res = await dispatch(createBrandSpecificationRequest(data));
    console.log('Created brand specification:', res);
    setReload((prev) => prev + 1);
  };

  const deleteBrandSpecification = async (rowId) => {
    await dispatch(deleteBrandSpecificationRequest(rowId));
    console.log('Deleted brand specification with id:', rowId);
    setReload((prev) => prev + 1);
  };

  if (!loading) {
    return (
      <Box>
        <h1>{specification?.name}</h1>
        <Divider sx={{ width: '100%', mb: 5, mt: 5, bgcolor: '#C09A53' }} />
        <Box component="form" onSubmit={handleEditBasicData}>
          <TextField
            sx={{ mr: 2, mb: 2, width: '40%' }}
            label={t('masterData.description')}
            value={name}
            onChange={(e) => setName(e.target.value)}
          />
          <TextField
            sx={{ mr: 2, mb: 2 }}
            label={t('masterData.specification.unit')}
            value={unit}
            onChange={(e) => setUnit(e.target.value)}
          />
          <Autocomplete
            disablePortal
            sx={{ width: '500px', height: '70px' }}
            limitTags={2}
            options={DECISION_MAKERS}
            value={decisionMaker}
            onChange={(e, values) => setDecisionMaker(values)}
            renderInput={(params) => (
              <TextField
                sx={{ height: '70px' }}
                {...params}
                label={t('masterData.specification.decisionMaker')}
              />
            )}
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={isLineSpecific}
                onChange={(e) => setIsLineSpecific(e.target.checked)}
              />
            }
            sx={{ mt: 1 }}
            label={t('masterData.specification.isLineSpecific')}
          />
          <Button
            type="submit"
            variant="contained"
            sx={{ bgcolor: '#C09A53', ':hover': { bgcolor: '#9c7c43' }, mt: 1 }}
          >
            {t('masterData.button.saveChanges')}
          </Button>
        </Box>

        <Divider sx={{ width: '100%', mb: 5, mt: 5, bgcolor: '#C09A53' }} />

        <Box component="form" onSubmit={handleSubmit}>
          <h2>Dodaj specyfikcję dla gatunku</h2>
          <Autocomplete
            disablePortal
            multiple
            sx={{ width: '500px', height: '70px' }}
            limitTags={2}
            options={brands.map((el) => ({
              id: el.id,
              label: el.description,
            }))}
            onChange={(e, values) => setBrandId(values.map((v) => v.id))}
            renderInput={(params) => (
              <TextField
                sx={{ height: '70px' }}
                {...params}
                label={t('masterData.brand')}
              />
            )}
          />
          <Box sx={{ display: 'flex', gap: 1, mb: 1 }}>
            <FormControl
              sx={{ width: '300px', height: '70px' }}
              size="medium"
              variant="outlined"
              disabled={!isLineSpecific}
            >
              <InputLabel shrink>
                {t('masterData.specification.whLine')}
              </InputLabel>
              <OutlinedInput
                sx={{ height: '70px' }}
                notched
                label={t('masterData.specification.whLine')}
                startAdornment={
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <Tooltip title={t('masterData.warehouse')}>
                      <TextField
                        sx={{ width: '135px' }}
                        disabled={!isLineSpecific}
                        size="small"
                        label={t('masterData.warehouse')}
                        value={newWarehouse}
                        onChange={(e) => setNewWarehouse(e.target.value)}
                      />
                    </Tooltip>
                    <Tooltip title={t('masterData.line')}>
                      <TextField
                        sx={{ width: '135px' }}
                        disabled={!isLineSpecific}
                        size="small"
                        label={t('masterData.line')}
                        value={newLine}
                        onChange={(e) => setNewLine(e.target.value)}
                      />
                    </Tooltip>
                  </Box>
                }
                inputProps={{ readOnly: true }}
              />
            </FormControl>
            <FormControl
              sx={{ width: '250px', height: '70px' }}
              size="medium"
              variant="outlined"
            >
              <InputLabel shrink>
                {t('masterData.specification.minX')}
              </InputLabel>
              <OutlinedInput
                sx={{ height: '70px' }}
                notched
                label={t('masterData.specification.minX')}
                startAdornment={
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <TextField
                      sx={{ width: '110px' }}
                      size="small"
                      label={t('masterData.specification.minValueX')}
                      value={newMinXMin}
                      onChange={(e) => setNewMinXMin(e.target.value)}
                    />

                    <TextField
                      sx={{ width: '110px' }}
                      size="small"
                      label={t('masterData.specification.maxValueX')}
                      value={newMinXMax}
                      onChange={(e) => setNewMinXMax(e.target.value)}
                    />
                  </Box>
                }
                inputProps={{ readOnly: true }}
              />
            </FormControl>
            <FormControl
              sx={{ width: '250px', height: '70px' }}
              size="medium"
              variant="outlined"
            >
              <InputLabel shrink>
                {t('masterData.specification.minB')}
              </InputLabel>
              <OutlinedInput
                sx={{ height: '70px' }}
                notched
                label={t('masterData.specification.minB')}
                startAdornment={
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <TextField
                      sx={{ width: '110px' }}
                      size="small"
                      label={t('masterData.specification.minValueB')}
                      value={newMinBMin}
                      onChange={(e) => setNewMinBMin(e.target.value)}
                    />

                    <TextField
                      sx={{ width: '110px' }}
                      size="small"
                      label={t('masterData.specification.maxValueB')}
                      value={newMinBMax}
                      onChange={(e) => setNewMinBMax(e.target.value)}
                    />
                  </Box>
                }
                inputProps={{ readOnly: true }}
              />
            </FormControl>
            <FormControl
              sx={{ width: '405px', height: '70px' }}
              size="medium"
              variant="outlined"
            >
              <InputLabel shrink>
                {t('masterData.specification.defaultValue')}
              </InputLabel>
              <OutlinedInput
                sx={{ height: '70px' }}
                notched
                label={t('masterData.specification.defaultValue')}
                startAdornment={
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <TextField
                      sx={{ width: '120px' }}
                      size="small"
                      label={t('masterData.specification.minDefaultValue')}
                      value={newSpecMin}
                      onChange={(e) => setNewSpecMin(e.target.value)}
                    />
                    <TextField
                      sx={{ width: '120px' }}
                      size="small"
                      label={t('masterData.specification.nominalDefaultValue')}
                      value={newSpecNominal}
                      onChange={(e) => setNewSpecNominal(e.target.value)}
                    />

                    <TextField
                      sx={{ width: '120px' }}
                      size="small"
                      label={t('masterData.specification.maxDefaultValue')}
                      value={newSpecMax}
                      onChange={(e) => setNewSpecMax(e.target.value)}
                    />
                  </Box>
                }
                inputProps={{ readOnly: true }}
              />
            </FormControl>
            <FormControl
              sx={{ width: '250px', height: '70px' }}
              size="medium"
              variant="outlined"
            >
              <InputLabel shrink>
                {t('masterData.specification.maxB')}
              </InputLabel>
              <OutlinedInput
                sx={{ height: '70px' }}
                notched
                label={t('masterData.specification.maxB')}
                startAdornment={
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <TextField
                      sx={{ width: '110px' }}
                      size="small"
                      label={t('masterData.specification.minValueB')}
                      value={newMaxBMin}
                      onChange={(e) => setNewMaxBMin(e.target.value)}
                    />

                    <TextField
                      sx={{ width: '110px' }}
                      size="small"
                      label={t('masterData.specification.maxValueB')}
                      value={newMaxBMax}
                      onChange={(e) => setNewMaxBMax(e.target.value)}
                    />
                  </Box>
                }
                inputProps={{ readOnly: true }}
              />
            </FormControl>
            <FormControl
              sx={{ width: '250px', height: '70px' }}
              size="medium"
              variant="outlined"
            >
              <InputLabel shrink>
                {t('masterData.specification.maxX')}
              </InputLabel>
              <OutlinedInput
                sx={{ height: '70px' }}
                notched
                label={t('masterData.specification.maxX')}
                startAdornment={
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <TextField
                      sx={{ width: '110px' }}
                      size="small"
                      label={t('masterData.specification.minValueX')}
                      value={newMaxXMin}
                      onChange={(e) => setNewMaxXMin(e.target.value)}
                    />

                    <TextField
                      sx={{ width: '110px' }}
                      size="small"
                      label={t('masterData.specification.maxValueX')}
                      value={newMaxXMax}
                      onChange={(e) => setNewMaxXMax(e.target.value)}
                    />
                  </Box>
                }
                inputProps={{ readOnly: true }}
              />
            </FormControl>
          </Box>
          <Button
            type="submit"
            variant="contained"
            sx={{ bgcolor: '#C09A53', ':hover': { bgcolor: '#9c7c43' }, mt: 1 }}
          >
            {t('masterData.button.add')}
          </Button>
        </Box>
        <Divider sx={{ width: '100%', mb: 5, mt: 5, bgcolor: '#C09A53' }} />
        <TableContainer component={Paper}>
          <Table sx={{ minWidth: 650 }} size="small" aria-label="reason table">
            <TableHead>
              <TableRow>
                <TableCell sx={{ width: '20%' }}>
                  {t('masterData.brand')}
                </TableCell>
                <TableCell>{t('masterData.specification.whLine')}</TableCell>
                <TableCell>{t('masterData.specification.unit')}</TableCell>
                <TableCell sx={{ textAlign: 'center' }}>
                  {t('masterData.specification.minX')}
                </TableCell>
                <TableCell sx={{ textAlign: 'center' }}>
                  {t('masterData.specification.minB')}
                </TableCell>
                <TableCell sx={{ textAlign: 'center' }}>
                  {t('masterData.specification.defaultValue')}
                </TableCell>
                <TableCell sx={{ textAlign: 'center' }}>
                  {t('masterData.specification.maxB')}
                </TableCell>
                <TableCell sx={{ textAlign: 'center' }}>
                  {t('masterData.specification.maxX')}
                </TableCell>

                {user?.users?.role === 'ADMIN' && (
                  <TableCell align="center">{t('account.action')}</TableCell>
                )}
              </TableRow>
            </TableHead>
            <TableBody>
              {specification?.labSpecification?.map((el) => {
                const zMinX = el.labZones?.find(
                  (z) => z.descriptionOrder === 'minX',
                );
                const zMinB = el.labZones?.find(
                  (z) => z.descriptionOrder === 'minB',
                );
                const zMaxB = el.labZones?.find(
                  (z) => z.descriptionOrder === 'maxB',
                );
                const zMaxX = el.labZones?.find(
                  (z) => z.descriptionOrder === 'maxX',
                );
                const isEditingRow = editRowId === el.id;

                return (
                  <>
                    <TableRow
                      key={el.id}
                      sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
                    >
                      <TableCell component="th">
                        {el.brand.description}
                      </TableCell>
                      <TableCell component="th">
                        {el.warehouse ? `${el.warehouse} / L${el.line}` : ''}
                      </TableCell>
                      <TableCell>{specification.unit}</TableCell>

                      <TableCell sx={{ textAlign: 'center' }}>
                        {zMinX?.rangeLabel ?? ''}
                      </TableCell>
                      <TableCell sx={{ textAlign: 'center' }}>
                        {zMinB?.rangeLabel ?? ''}
                      </TableCell>
                      <TableCell sx={{ textAlign: 'center' }}>
                        {el.specLabel}
                      </TableCell>
                      <TableCell sx={{ textAlign: 'center' }}>
                        {zMaxB?.rangeLabel ?? ''}
                      </TableCell>
                      <TableCell sx={{ textAlign: 'center' }}>
                        {zMaxX?.rangeLabel ?? ''}
                      </TableCell>

                      {user?.users?.role === 'ADMIN' && (
                        <TableCell>
                          <Box
                            sx={{ display: 'flex', justifyContent: 'center' }}
                          >
                            <Button
                              sx={{ color: 'orange' }}
                              onClick={() => {
                                startEditing(el);
                              }}
                            >
                              <EditIcon />
                            </Button>
                            <Button
                              sx={{ color: 'red' }}
                              onClick={() => deleteBrandSpecification(el.id)}
                            >
                              <DeleteIcon />
                            </Button>
                          </Box>
                        </TableCell>
                      )}
                    </TableRow>

                    {isEditingRow && (
                      <TableRow>
                        <TableCell>{el.brand.description}</TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', gap: 1, width: '150px' }}>
                            <Tooltip title={t('masterData.warehouse')}>
                              <TextField
                                disabled={!isLineSpecific}
                                size="small"
                                label={t('masterData.warehouse')}
                                value={warehouse}
                                onChange={(e) => setWarehouse(e.target.value)}
                              />
                            </Tooltip>

                            <Tooltip title={t('masterData.line')}>
                              <TextField
                                disabled={!isLineSpecific}
                                size="small"
                                label={t('masterData.line')}
                                value={line}
                                onChange={(e) => setLine(e.target.value)}
                              />
                            </Tooltip>
                          </Box>
                        </TableCell>
                        <TableCell>{specification.unit}</TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', gap: 1 }}>
                            <TextField
                              size="small"
                              label="Min"
                              sx={{ width: 90 }}
                              value={minXMin}
                              onChange={(e) => setMinXMin(e.target.value)}
                            />
                            <TextField
                              size="small"
                              label="Max"
                              sx={{ width: 90 }}
                              value={minXMax}
                              onChange={(e) => setMinXMax(e.target.value)}
                            />
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', gap: 1 }}>
                            <TextField
                              size="small"
                              label="Min"
                              sx={{ width: 90 }}
                              value={minBMin}
                              onChange={(e) => setMinBMin(e.target.value)}
                            />
                            <TextField
                              size="small"
                              label="Max"
                              sx={{ width: 90 }}
                              value={minBMax}
                              onChange={(e) => setMinBMax(e.target.value)}
                            />
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', gap: 1 }}>
                            <TextField
                              size="small"
                              label="Min"
                              sx={{ width: 90 }}
                              value={specMin}
                              onChange={(e) => setSpecMin(e.target.value)}
                            />
                            <TextField
                              size="small"
                              label="Nominal"
                              sx={{ width: 90 }}
                              value={specNominal}
                              onChange={(e) => setSpecNominal(e.target.value)}
                            />
                            <TextField
                              size="small"
                              label="Max"
                              sx={{ width: 90 }}
                              value={specMax}
                              onChange={(e) => setSpecMax(e.target.value)}
                            />
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', gap: 1 }}>
                            <TextField
                              size="small"
                              label="Min"
                              sx={{ width: 90 }}
                              value={maxBMin}
                              onChange={(e) => setMaxBMin(e.target.value)}
                            />
                            <TextField
                              size="small"
                              label="Max"
                              sx={{ width: 90 }}
                              value={maxBMax}
                              onChange={(e) => setMaxBMax(e.target.value)}
                            />
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', gap: 1 }}>
                            <TextField
                              size="small"
                              label="Min"
                              sx={{ width: 90 }}
                              value={maxXMin}
                              onChange={(e) => setMaxXMin(e.target.value)}
                            />
                            <TextField
                              size="small"
                              label="Max"
                              sx={{ width: 90 }}
                              value={maxXMax}
                              onChange={(e) => setMaxXMax(e.target.value)}
                            />
                          </Box>
                        </TableCell>
                        <TableCell align="center">
                          <Box
                            sx={{
                              display: 'flex',
                              justifyContent: 'center',
                              gap: 1,
                            }}
                          >
                            <Button
                              variant="contained"
                              size="small"
                              onClick={handleEditBrandSpecification}
                            >
                              {t('masterData.button.save')}
                            </Button>
                            <Button
                              variant="outlined"
                              size="small"
                              onClick={() => cancelEditing()}
                            >
                              {t('masterData.button.cancel')}
                            </Button>
                          </Box>
                        </TableCell>
                      </TableRow>
                    )}
                  </>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>
    );
  }
};

export default EditSpecification;
